<template>
  <div class="min-h-screen bg-base-100 py-8">
    <div class="container mx-auto px-4 max-w-4xl">
      <!-- Header -->
      <div class="text-center mb-8">
        <h1 class="text-4xl font-bold text-base-content mb-4">{{ $t("emailConfig.title") }}</h1>
        <p class="text-lg text-base-content/70">{{ $t("emailConfig.subtitle") }}</p>
      </div>

      <!-- Email Input -->
      <div class="card bg-base-200 shadow-xl mb-8">
        <div class="card-body">
          <h2 class="card-title">{{ $t("emailConfig.emailInput.title") }}</h2>
          <div class="form-control">
            <label class="label">
              <span class="label-text">{{ $t("emailConfig.emailInput.label") }}</span>
            </label>
            <input
              v-model="emailAddress"
              type="email"
              :placeholder="$t('emailConfig.emailInput.placeholder')"
              class="input input-bordered w-full"
              @input="updateUrl" />
          </div>
          <p class="text-sm text-base-content/60 mt-2">
            {{ $t("emailConfig.emailInput.description") }}
          </p>
        </div>
      </div>

      <!-- Configuration Cards -->
      <div class="grid gap-6 md:grid-cols-2">
        <!-- Automatic Configuration -->
        <div class="card bg-base-200 shadow-xl">
          <div class="card-body">
            <h2 class="card-title text-primary">{{ $t("emailConfig.automatic.title") }}</h2>
            <p class="text-base-content/70 mb-4">{{ $t("emailConfig.automatic.description") }}</p>

            <div class="space-y-3">
              <button
                @click="goToIosTutorial"
                class="btn btn-outline btn-success w-full justify-start">
                🍎
                {{ $t("emailConfig.automatic.iosMail") }}
              </button>

              <button
                @click="goToGmailTutorial"
                class="btn btn-outline btn-error w-full justify-start">
                📬 Gmail
              </button>
            </div>
          </div>
        </div>

        <!-- Manual Configuration -->
        <div class="card bg-base-200 shadow-xl">
          <div class="card-body">
            <h2 class="card-title text-secondary">{{ $t("emailConfig.manual.title") }}</h2>
            <p class="text-base-content/70 mb-4">{{ $t("emailConfig.manual.description") }}</p>

            <div class="space-y-4">
              <!-- Incoming Server -->
              <div class="bg-base-300 p-4 rounded-lg">
                <h3 class="font-semibold text-base-content mb-2">{{ $t("emailConfig.manual.incoming.title") }}</h3>
                <div class="space-y-1 text-sm">
                  <div class="flex justify-between">
                    <span class="text-base-content/70">{{ $t("emailConfig.manual.incoming.server") }}:</span>
                    <span class="font-mono">{{ mailHost }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-base-content/70">{{ $t("emailConfig.manual.incoming.port") }}:</span>
                    <span class="font-mono">993</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-base-content/70">{{ $t("emailConfig.manual.incoming.security") }}:</span>
                    <span class="font-mono">SSL/TLS</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-base-content/70">{{ $t("emailConfig.manual.incoming.username") }}:</span>
                    <span class="font-mono">{{ emailAddress || "your-email@domain.com" }}</span>
                  </div>
                </div>
              </div>

              <!-- Outgoing Server -->
              <div class="bg-base-300 p-4 rounded-lg">
                <h3 class="font-semibold text-base-content mb-2">{{ $t("emailConfig.manual.outgoing.title") }}</h3>
                <div class="space-y-1 text-sm">
                  <div class="flex justify-between">
                    <span class="text-base-content/70">{{ $t("emailConfig.manual.outgoing.server") }}:</span>
                    <span class="font-mono">{{ mailHost }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-base-content/70">{{ $t("emailConfig.manual.outgoing.port") }}:</span>
                    <span class="font-mono">465</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-base-content/70">{{ $t("emailConfig.manual.outgoing.security") }}:</span>
                    <span class="font-mono">SSL/TLS</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-base-content/70">{{ $t("emailConfig.manual.outgoing.auth") }}:</span>
                    <span class="font-mono">{{ $t("emailConfig.manual.outgoing.authRequired") }}</span>
                  </div>
                </div>
              </div>

              <!-- Copy Button -->
              <button @click="copyConfiguration" class="btn btn-secondary w-full">
                📋
                {{ $t("emailConfig.manual.copy") }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Instructions -->
      <div class="card bg-base-200 shadow-xl mt-8">
        <div class="card-body">
          <h2 class="card-title">{{ $t("emailConfig.instructions.title") }}</h2>
          <div class="prose max-w-none">
            <ol class="space-y-3">
              <li class="flex items-start gap-3">
                <span
                  class="flex-shrink-0 w-6 h-6 bg-primary text-primary-content rounded-full flex items-center justify-center text-sm font-bold"
                  >1</span
                >
                <span>{{ $t("emailConfig.instructions.step1") }}</span>
              </li>
              <li class="flex items-start gap-3">
                <span
                  class="flex-shrink-0 w-6 h-6 bg-primary text-primary-content rounded-full flex items-center justify-center text-sm font-bold"
                  >2</span
                >
                <span>{{ $t("emailConfig.instructions.step2") }}</span>
              </li>
              <li class="flex items-start gap-3">
                <span
                  class="flex-shrink-0 w-6 h-6 bg-primary text-primary-content rounded-full flex items-center justify-center text-sm font-bold"
                  >3</span
                >
                <span>{{ $t("emailConfig.instructions.step3") }}</span>
              </li>
              <li class="flex items-start gap-3">
                <span
                  class="flex-shrink-0 w-6 h-6 bg-primary text-primary-content rounded-full flex items-center justify-center text-sm font-bold"
                  >4</span
                >
                <span>{{ $t("emailConfig.instructions.step4") }}</span>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { getMailHost } from "@/utils/emailHosts";
import { useEmailSetupRoute } from "@/composables/useEmailSetupRoute";

const emailAddress = ref<string>("");
const mailHost = computed(() => getMailHost(emailAddress.value));
const { replaceQuery, pushTutorial } = useEmailSetupRoute({ emailRef: emailAddress });

const updateUrl = () => {
  replaceQuery({ email: emailAddress.value });
};

const goToIosTutorial = () => {
  pushTutorial("/emailsetup/ios-tutorial");
};

const goToGmailTutorial = () => {
  pushTutorial("/emailsetup/gmail-tutorial");
};

const copyConfiguration = async () => {
  const config = `
Email Configuration for ${emailAddress.value || "your-email@domain.com"}

Incoming Server (IMAP):
- Server: ${mailHost.value}
- Port: 993
- Security: SSL/TLS
- Username: ${emailAddress.value || "your-email@domain.com"}

Outgoing Server (SMTP):
- Server: ${mailHost.value}
- Port: 465
- Security: SSL/TLS
- Authentication: Required
- Username: ${emailAddress.value || "your-email@domain.com"}
  `.trim();

  try {
    await navigator.clipboard.writeText(config);
  } catch (err) {
    console.error("Failed to copy configuration:", err);
  }
};
</script>
